'use client'

import {
  type MouseEventHandler,
  type ReactNode,
  type TouchEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LinkIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline'
import {
  getGalleryImageUrl,
  getGalleryPhotoDownloadUrl,
  getGalleryPhotoLabel,
} from '../utils'

interface Photo {
  id: number
  imageUrl: string
  title?: string | null
  description?: string | null
}

interface PhotoModalProps {
  photos: Photo[]
  currentIndex: number
  currentPhotoNumber: number
  photoUrl: string | null
  totalPhotos: number
  onClose: () => void
  onNavigate: (direction: 'prev' | 'next') => void
  isLoadingMore?: boolean
}

const IMAGE_BOUNDS_CLASS =
  'max-h-[calc(100vh-7rem)] max-w-[calc(100vw-1rem)] md:max-h-[calc(100vh-8rem)] md:max-w-[calc(100vw-10rem)]'
const ICON_BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-lg bg-black/60 p-2 text-white backdrop-blur-sm transition-colors duration-200 hover:bg-black/75'
const ACTION_BUTTON_CLASS =
  'inline-flex items-center gap-1.5 rounded-lg bg-black/60 px-3 py-2 text-sm font-medium text-white backdrop-blur-sm transition-colors duration-200 hover:bg-black/75'
const IMAGE_CLOSE_BUTTON_CLASS =
  'absolute right-2 top-2 z-20 rounded-full bg-black/55 p-2 text-white backdrop-blur-sm transition-colors duration-200 hover:bg-black/75'

interface IconButtonProps {
  ariaLabel: string
  children: ReactNode
  onClick: MouseEventHandler<HTMLButtonElement>
}

const IconButton = ({ ariaLabel, children, onClick }: IconButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={ICON_BUTTON_CLASS}
    aria-label={ariaLabel}
  >
    {children}
  </button>
)

export const PhotoModal = ({
  photos,
  currentIndex,
  currentPhotoNumber,
  photoUrl,
  totalPhotos,
  onClose,
  onNavigate,
  isLoadingMore = false,
}: PhotoModalProps) => {
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const [copiedPhotoUrl, setCopiedPhotoUrl] = useState<string | null>(null)
  const [loadedPhotoId, setLoadedPhotoId] = useState<number | null>(null)
  const currentPhoto = photos[currentIndex]
  const hasCopiedLink = copiedPhotoUrl === photoUrl
  const hasLoadedCurrentPhoto = loadedPhotoId === currentPhoto?.id
  const downloadUrl = currentPhoto
    ? getGalleryPhotoDownloadUrl(currentPhoto)
    : null

  const handleTouchStart: TouchEventHandler<HTMLDivElement> = event => {
    const touch = event.touches[0]

    touchStartRef.current = touch
      ? { x: touch.clientX, y: touch.clientY }
      : null
  }

  const handleTouchEnd: TouchEventHandler<HTMLDivElement> = event => {
    const touchStart = touchStartRef.current
    const touch = event.changedTouches[0]
    touchStartRef.current = null

    if (!touchStart || !touch || !hasLoadedCurrentPhoto) {
      return
    }

    const horizontalDistance = touch.clientX - touchStart.x
    const verticalDistance = touch.clientY - touchStart.y

    if (
      Math.abs(horizontalDistance) < 50 ||
      Math.abs(horizontalDistance) <= Math.abs(verticalDistance)
    ) {
      return
    }

    onNavigate(horizontalDistance < 0 ? 'next' : 'prev')
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft' && hasLoadedCurrentPhoto) {
        onNavigate('prev')
      } else if (e.key === 'ArrowRight' && hasLoadedCurrentPhoto) {
        onNavigate('next')
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [hasLoadedCurrentPhoto, onClose, onNavigate])

  useEffect(() => {
    const originalBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalBodyOverflow
    }
  }, [])

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current)
      }
    }
  }, [])

  const copyPhotoLink = async () => {
    if (!photoUrl) {
      return
    }

    await navigator.clipboard.writeText(photoUrl)
    setCopiedPhotoUrl(photoUrl)

    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current)
    }

    copiedTimeoutRef.current = setTimeout(() => {
      setCopiedPhotoUrl(currentPhotoUrl =>
        currentPhotoUrl === photoUrl ? null : currentPhotoUrl
      )
    }, 1600)
  }

  if (!currentPhoto) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative flex h-full w-full items-center justify-center p-2 md:p-8"
        onClick={e => e.stopPropagation()}
      >
        {!hasLoadedCurrentPhoto && (
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-20 rounded-full bg-black/55 p-2 text-white backdrop-blur-sm transition-colors duration-200 hover:bg-black/75"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}

        <div className="flex max-h-full flex-col items-center gap-3">
          <div
            className="relative inline-flex min-h-64 min-w-48 touch-pan-y items-center justify-center"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={() => {
              touchStartRef.current = null
            }}
          >
            <img
              src={getGalleryImageUrl(currentPhoto.imageUrl, 96)}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 h-full w-full scale-105 rounded-sm object-cover blur-xl transition-opacity duration-300 ${
                hasLoadedCurrentPhoto ? 'opacity-0' : 'opacity-70'
              }`}
            />
            {!hasLoadedCurrentPhoto && (
              <div className="absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-3 rounded-lg bg-black/35 px-5 py-4 text-white backdrop-blur-sm">
                <div className="relative">
                  <PhotoIcon className="h-8 w-8 text-white/80" />
                  <ArrowPathIcon className="absolute -right-2 -top-2 h-4 w-4 animate-spin text-white/80" />
                </div>
                <span className="text-sm font-medium">Loading photo</span>
              </div>
            )}
            <img
              src={getGalleryImageUrl(currentPhoto.imageUrl, 1600)}
              alt={getGalleryPhotoLabel(currentPhoto)}
              className={`relative block rounded-sm object-contain shadow-2xl transition-opacity duration-300 ${IMAGE_BOUNDS_CLASS} ${
                hasLoadedCurrentPhoto ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setLoadedPhotoId(currentPhoto.id)}
            />
            {hasLoadedCurrentPhoto && (
              <button
                type="button"
                onClick={onClose}
                className={IMAGE_CLOSE_BUTTON_CLASS}
                aria-label="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            )}
          </div>

          {hasLoadedCurrentPhoto && (
            <div className="flex w-full flex-col items-center gap-3">
              {(currentPhoto.title || currentPhoto.description) && (
                <div className="w-full max-w-3xl rounded-lg bg-black/60 p-4 text-white backdrop-blur-sm">
                  {currentPhoto.title && (
                    <h3 className="mb-2 text-xl font-semibold">
                      {currentPhoto.title}
                    </h3>
                  )}
                  {currentPhoto.description && (
                    <p className="text-sm text-gray-300">
                      {currentPhoto.description}
                    </p>
                  )}
                </div>
              )}

              <div className="flex max-w-[calc(100vw-1rem)] flex-wrap items-center justify-center gap-2">
                <IconButton
                  onClick={e => {
                    e.stopPropagation()
                    onNavigate('prev')
                  }}
                  ariaLabel="Previous photo"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </IconButton>

                <IconButton
                  onClick={e => {
                    e.stopPropagation()
                    onNavigate('next')
                  }}
                  ariaLabel="Next photo"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </IconButton>

                <div className="rounded-lg bg-black/60 px-3 py-2 text-sm text-white backdrop-blur-sm">
                  {currentPhotoNumber} / {totalPhotos}
                  {isLoadingMore && ' - Loading more photos...'}
                </div>
                {photoUrl && (
                  <button
                    type="button"
                    onClick={copyPhotoLink}
                    className={ACTION_BUTTON_CLASS}
                  >
                    {hasCopiedLink ? (
                      <CheckIcon className="h-4 w-4" />
                    ) : (
                      <LinkIcon className="h-4 w-4" />
                    )}
                    {hasCopiedLink ? 'Copied' : 'Copy link'}
                  </button>
                )}
                {downloadUrl && (
                  <a href={downloadUrl} className={ACTION_BUTTON_CLASS}>
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Download
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
